import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * Body text sizes for different viewport presets:
 * - xs: text-xs (smallest, rarely used)
 * - sm: text-sm (tablet portrait)
 * - default: text-base (tablet landscape, iPad Pro)
 * - lg: text-lg (large tablet, iPad Pro)
 * - xl: text-xl (touchscreen PC, 4K)
 */
export type BodySize = "xs" | "sm" | "default" | "lg" | "xl";

const bodyVariants = cva("", {
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      default: "text-base",
      lg: "text-lg",
      xl: "text-xl",
    },
    intent: {
      default: "text-foreground",
      muted: "text-muted-foreground",
      error: "text-destructive",
      success: "text-green-600 dark:text-green-500",
      warning: "text-amber-600 dark:text-amber-500",
      primary: "text-primary",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
  },
  defaultVariants: {
    size: "default",
    intent: "default",
    weight: "normal",
    align: "left",
  },
});

export interface BodyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "children">,
    VariantProps<typeof bodyVariants> {
  /**
   * The HTML element to render. Defaults to "p".
   */
  as?: "p" | "span" | "div" | "label";
  children: React.ReactNode;
}

/**
 * Body component for standard paragraph text throughout the app.
 * Use for general content, descriptions, and instructions.
 *
 * @example
 * ```tsx
 * <Body>Geben Sie Ihre Daten ein und klicken Sie auf Suchen</Body>
 * <Body intent="muted">Noch nicht bei uns registriert?</Body>
 * <Body intent="error">Bitte füllen Sie dieses Feld aus</Body>
 * ```
 */
function Body({
  className,
  as: Component = "p",
  size,
  intent,
  weight,
  align,
  children,
  ...props
}: BodyProps) {
  return (
    <Component
      className={cn(bodyVariants({ size, intent, weight, align }), className)}
      {...props}
    >
      {children}
    </Component>
  );
}

Body.displayName = "Body";

export { Body };
